/*
 * Confetti.jsx
 * ------------
 * Full screen confetti burst animation triggered when a checklist
 * is fully completed. Renders colored particles that fall and spin
 * then removes itself from the DOM after the animation completes.
 * Pure CSS animation, no external libraries.
 * Accepts an onComplete callback fired when the animation finishes.
 */

import React, { useEffect, useState, useRef } from "react";

// ============================================
// CONFETTI CONFIG
// ============================================
const PARTICLE_COUNT = 80;
const DURATION = 3000; // ms

const COLORS = [
  "var(--accent-green)",
  "var(--accent-green-light)",
  "var(--accent-gold)",
  "var(--accent-gold-light)",
  "#FFFFFF",
  "var(--accent-green-muted)",
  "var(--accent-gold-muted)",
];

const SHAPES = ["square", "circle", "rectangle"];

// ============================================
// ANIMATION STYLES
// Injected once into document head
// ============================================
const CONFETTI_STYLE = `
  @keyframes confettiFall {
    0% {
      transform: translateY(-20px) rotate(0deg) scale(1);
      opacity: 1;
    }
    80% {
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg) scale(0.5);
      opacity: 0;
    }
  }

  @keyframes confettiSway {
    0%, 100% { margin-left: 0px; }
    25%       { margin-left: 30px; }
    75%       { margin-left: -30px; }
  }
`;

let confettiStyleInjected = false;
function injectConfettiStyle() {
  if (confettiStyleInjected) return;
  const style = document.createElement("style");
  style.textContent = CONFETTI_STYLE;
  document.head.appendChild(style);
  confettiStyleInjected = true;
}

// ============================================
// PARTICLE GENERATOR
// ============================================
function generateParticles() {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const color = COLORS[Math.floor(Math.random() * COLORS.length)];
    const size = Math.random() * 10 + 6; // 6px to 16px
    const left = Math.random() * 100; // 0% to 100% of viewport width
    const delay = Math.random() * 1000; // 0ms to 1000ms
    const duration = DURATION + Math.random() * 1000; // vary duration slightly
    const swayDuration = 1000 + Math.random() * 1000;

    return {
      id: i,
      shape,
      color,
      size,
      left,
      delay,
      duration,
      swayDuration
    };
  });
}

// ============================================
// SINGLE PARTICLE
// ============================================
function Particle({ shape, color, size, left, delay, duration, swayDuration }) {
  const shapeStyles = {
    square: {
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "2px"
    },
    circle: {
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: "50%"
    },
    rectangle: {
      width: `${size * 1.8}px`,
      height: `${size * 0.6}px`,
      borderRadius: "2px"
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: `${left}%`,
        animation: `
          confettiSway ${swayDuration}ms ease-in-out infinite,
          confettiFall ${duration}ms ${delay}ms ease-in forwards
        `,
        ...shapeStyles[shape],
        background: color,
        willChange: "transform, opacity"
      }}
    />
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Confetti({ onComplete }) {
  const [particles] = useState(() => generateParticles());
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    injectConfettiStyle();

    // Remove confetti after animation completes
    timerRef.current = setTimeout(() => {
      setVisible(false);
      if (onComplete) onComplete();
    }, DURATION + 1500); // extra buffer for staggered particles

    return () => clearTimeout(timerRef.current);
  }, [onComplete]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9998,
        overflow: "hidden"
      }}
    >
      {particles.map(particle => (
        <Particle key={particle.id} {...particle} />
      ))}
    </div>
  );
}